<template>
  <div
    class="min-h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 fixed inset-0 overflow-y-auto pt-16">
    <!-- Header équilibré -->
    <div class="relative overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200">
      <div class="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
      <div class="relative container mx-auto px-6 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {{$t('dashboard.title')}}
            </h1>
            <p class="mt-1 text-gray-600">Gérez vos fiches MEMoS et vos données patrimoniales</p>
          </div>
          <div class="hidden md:flex items-center space-x-4">
            <div class="flex items-center space-x-2 text-sm text-gray-500">
              <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Connecté</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-6 py-6 pb-16">
      <!-- Section Fiches MEMoS -->
      <div class="mb-8">
        <div class="flex items-center mb-6">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <ClipboardListIcon class="w-5 h-5 text-white" />
            </div>
            <h2 class="text-xl font-semibold text-gray-900">Fiches MEMoS</h2>
          </div>
          <div class="ml-auto hidden sm:block">
            <span class="text-sm text-gray-500">Créez et gérez vos mémorisations</span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Nouvelle fiche -->
          <router-link to="/form" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-blue-300 h-28">
              <div class="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60"></div>
              <div class="relative p-6 flex items-center justify-between h-full">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <PlusCircleIcon class="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      Créer une fiche
                    </h3>
                    <p class="text-sm text-gray-500">Nouvelle mémorisation</p>
                  </div>
                </div>
                <ArrowRightIcon
                  class="w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-300" />
              </div>
            </div>
          </router-link>

          <!-- Importer des fiches -->
          <router-link to="/import-memos" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-purple-300 h-28">
              <div class="absolute inset-0 bg-gradient-to-br from-purple-50 to-violet-50 opacity-60"></div>
              <div class="relative p-6 flex items-center justify-between h-full">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <UploadIcon class="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                      Importer un MEMoS
                    </h3>
                    <p class="text-sm text-gray-500">JSON ou QR code</p>
                  </div>
                </div>
                <ArrowRightIcon
                  class="w-5 h-5 text-gray-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all duration-300" />
              </div>
            </div>
          </router-link>

          <!-- Fiches créées -->
          <router-link to="/gestion-memos" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-green-300 h-28">
              <div class="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 opacity-60"></div>
              <div class="relative p-6 flex items-center justify-between h-full">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <ClipboardListIcon class="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                      Gestion MEMoS
                    </h3>
                    <p class="text-sm text-gray-500">Organisation, collections, partages</p>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <div class="text-2xl font-bold text-green-600">{{ memosStats.my }}</div>
                  <ArrowRightIcon
                    class="w-5 h-5 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Section Données -->
      <div class="mb-8">
        <div class="flex items-center mb-6">
          <div class="flex items-center space-x-3">
            <div
              class="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
              <DatabaseIcon class="w-5 h-5 text-white" />
            </div>
            <h2 class="text-xl font-semibold text-gray-900">Données patrimoniales</h2>
          </div>
          <div class="ml-auto hidden sm:block">
            <span class="text-sm text-gray-500">Organisez vos références</span>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <!-- Lieux -->
          <router-link to="/mylocations" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-purple-300 h-32">
              <div class="absolute inset-0 bg-gradient-to-br from-purple-50 to-violet-50 opacity-60"></div>
              <div class="relative p-4 flex flex-col items-center justify-center h-full text-center">
                <div
                  class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-3">
                  <MapPinIcon class="w-6 h-6 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                  Lieux
                </h3>
                <div class="text-xl font-bold text-purple-600 mt-1">{{ locationsStats.my }}</div>
              </div>
            </div>
          </router-link>

          <!-- Agents -->
          <router-link to="/myagents" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-yellow-300 h-32">
              <div class="absolute inset-0 bg-gradient-to-br from-yellow-50 to-amber-50 opacity-60"></div>
              <div class="relative p-4 flex flex-col items-center justify-center h-full text-center">
                <div
                  class="w-12 h-12 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-3">
                  <UserIcon class="w-6 h-6 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-gray-900 group-hover:text-yellow-600 transition-colors">
                  Agents
                </h3>
                <div class="text-xl font-bold text-yellow-600 mt-1">{{ agentsStats.my }}</div>
              </div>
            </div>
          </router-link>

          <!-- Jeux de données -->
          <router-link to="/mydigitalentities" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-red-300 h-32">
              <div class="absolute inset-0 bg-gradient-to-br from-red-50 to-rose-50 opacity-60"></div>
              <div class="relative p-4 flex flex-col items-center justify-center h-full text-center">
                <div
                  class="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-3">
                  <DatabaseIcon class="w-6 h-6 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                  Datasets
                </h3>
                <div class="text-xl font-bold text-red-600 mt-1">{{ digitalEntitiesStats.my }}</div>
              </div>
            </div>
          </router-link>

          <!-- Objets d'étude -->
          <router-link to="/mymaterialentities" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-indigo-300 h-32">
              <div class="absolute inset-0 bg-gradient-to-br from-indigo-50 to-blue-50 opacity-60"></div>
              <div class="relative p-4 flex flex-col items-center justify-center h-full text-center">
                <div
                  class="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-3">
                  <BoxIcon class="w-6 h-6 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                  Objets
                </h3>
                <div class="text-xl font-bold text-indigo-600 mt-1">{{ materialEntitiesStats.my }}</div>
              </div>
            </div>
          </router-link>

          <!-- Projets -->
          <router-link to="/myprojects" class="group">
            <div
              class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-orange-300 h-32">
              <div class="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 opacity-60"></div>
              <div class="relative p-4 flex flex-col items-center justify-center h-full text-center">
                <div
                  class="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-3">
                  <CircuitBoard class="w-6 h-6 text-white" />
                </div>
                <h3 class="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                  Projets
                </h3>
                <div class="text-xl font-bold text-orange-600 mt-1">{{ projectsStats.my }}</div>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Section Gestion des Données et Outils Avancés combinés -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <!-- Gestion des Données -->
        <div>
          <div class="flex items-center mb-4">
            <div class="flex items-center space-x-2">
              <div
                class="w-6 h-6 bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg flex items-center justify-center">
                <DownloadIcon class="w-4 h-4 text-white" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Gestion des Données</h3>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-3">
            <!-- Export -->
            <router-link to="/export-data" class="group">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-gray-400 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-gray-50 to-slate-50 opacity-60"></div>
                <div class="relative p-4 flex items-center justify-between h-full">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <DownloadIcon class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 class="text-sm font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                        Exporter mes données
                      </h4>
                      <p class="text-xs text-gray-500">Sauvegarde complète</p>
                    </div>
                  </div>
                  <ArrowRightIcon
                    class="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </div>
            </router-link>

            <!-- Import -->
            <div class="group cursor-pointer relative" @mouseenter="showFutureFeatureTooltip = 'import'"
              @mouseleave="showFutureFeatureTooltip = null">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-gray-400 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-gray-50 to-slate-50 opacity-60"></div>
                <div class="relative p-4 flex items-center justify-between h-full">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <UploadIcon class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 class="text-sm font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                        Importer des données
                      </h4>
                      <p class="text-xs text-gray-500">Restauration/Migration</p>
                    </div>
                  </div>
                  <ArrowRightIcon
                    class="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <!-- Badge "À venir" -->
                <div
                  class="absolute top-2 right-2 bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full font-medium text-xs">
                  À venir
                </div>
              </div>
              <!-- Tooltip à venir -->
              <FutureFeatureTooltip v-if="showFutureFeatureTooltip === 'import'" title="Import Intelligent"
                description="Importez vos données depuis plusieurs formats avec mapping automatique" />
            </div>
          </div>
        </div>

        <!-- Outils Avancés -->
        <div>
          <div class="flex items-center mb-4">
            <div class="flex items-center space-x-2">
              <div
                class="w-6 h-6 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <TrendingUpIcon class="w-4 h-4 text-white" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Outils Avancés</h3>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <!-- Export avancé -->
            <router-link to="/export-tools" class="group">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-indigo-300 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 opacity-60"></div>
                <div class="relative p-3 flex flex-col items-center justify-center h-full text-center">
                  <div
                    class="w-8 h-8 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-1">
                    <DownloadIcon class="w-4 h-4 text-white" />
                  </div>
                  <h4 class="text-xs font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                    Export avancé
                  </h4>
                </div>
              </div>
            </router-link>

            <!-- Carte interactive -->
            <router-link to="/carte-interactive" class="group">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-300 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 opacity-60"></div>
                <div class="relative p-3 flex flex-col items-center justify-center h-full text-center">
                  <div
                    class="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-1">
                    <MapIcon class="w-4 h-4 text-white" />
                  </div>
                  <h4 class="text-xs font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                    Carte
                  </h4>
                </div>
              </div>
            </router-link>

            <!-- Graphique des relations -->
            <div class="group cursor-pointer relative" @mouseenter="showFutureFeatureTooltip = 'graph'"
              @mouseleave="showFutureFeatureTooltip = null">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60"></div>
                <div class="relative p-3 flex flex-col items-center justify-center h-full text-center">
                  <div
                    class="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-1">
                    <NetworkIcon class="w-4 h-4 text-white" />
                  </div>
                  <h4 class="text-xs font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    Relations
                  </h4>
                </div>
                <!-- Badge "À venir" -->
                <div
                  class="absolute top-1 right-1 bg-amber-400 text-amber-900 px-1 py-0.5 rounded-full font-medium text-xs">
                  À venir
                </div>
              </div>
              <!-- Tooltip à venir -->
              <FutureFeatureTooltip v-if="showFutureFeatureTooltip === 'graph'" title="Graphique des Relations"
                description="Visualisez les connexions entre vos MEMoS, agents, lieux et objets" />
            </div>

            <!-- Timeline (masqué pour le moment) -->
            <!--
            <div class="group cursor-pointer relative" @mouseenter="showFutureFeatureTooltip = 'timeline'"
              @mouseleave="showFutureFeatureTooltip = null">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-purple-300 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-purple-50 to-violet-50 opacity-60"></div>
                <div class="relative p-3 flex flex-col items-center justify-center h-full text-center">
                  <div
                    class="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-1">
                    <CalendarIcon class="w-4 h-4 text-white" />
                  </div>
                  <h4 class="text-xs font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                    Timeline
                  </h4>
                </div>

                <div
                  class="absolute top-1 right-1 bg-amber-400 text-amber-900 px-1 py-0.5 rounded-full font-medium text-xs">
                  À venir
                </div>
              </div>

              <FutureFeatureTooltip v-if="showFutureFeatureTooltip === 'timeline'" title="Timeline Chronologique"
                description="Visualisez l'évolution temporelle de vos données patrimoniales" />
            </div>
            -->

            <!-- Statistiques -->
            <div class="group cursor-pointer relative" @mouseenter="showFutureFeatureTooltip = 'stats'"
              @mouseleave="showFutureFeatureTooltip = null">
              <div
                class="relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-orange-300 h-20">
                <div class="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 opacity-60"></div>
                <div class="relative p-3 flex flex-col items-center justify-center h-full text-center">
                  <div
                    class="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-1">
                    <TrendingUpIcon class="w-4 h-4 text-white" />
                  </div>
                  <h4 class="text-xs font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                    Stats
                  </h4>
                </div>
                <!-- Badge "À venir" -->
                <div
                  class="absolute top-1 right-1 bg-amber-400 text-amber-900 px-1 py-0.5 rounded-full font-medium text-xs">
                  À venir
                </div>
              </div>
              <!-- Tooltip à venir -->
              <FutureFeatureTooltip v-if="showFutureFeatureTooltip === 'stats'" title="Statistiques Avancées"
                description="Analysez vos données avec des graphiques et métriques détaillées" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FutureFeatureTooltip from '@/components/UI/FutureFeatureTooltip.vue';
import { useAssetsCacheStore } from '@/stores/assetsCache';
import {
  ArrowRightIcon,
  BoxIcon,
  CircuitBoard,
  ClipboardListIcon,
  DatabaseIcon,
  DownloadIcon,
  MapIcon,
  MapPinIcon,
  NetworkIcon,
  PlusCircleIcon,
  TrendingUpIcon,
  UploadIcon,
  UserIcon
} from "lucide-vue-next";
import { computed, onMounted, ref } from 'vue';

// Store
const assetsCacheStore = useAssetsCacheStore();

// État local pour les tooltips
const showFutureFeatureTooltip = ref<string | null>(null);

// Statistiques réactives depuis le store
const memosStats = computed(() => assetsCacheStore.memosStats);
const locationsStats = computed(() => assetsCacheStore.locationsStats);
const agentsStats = computed(() => assetsCacheStore.agentsStats);
const digitalEntitiesStats = computed(() => assetsCacheStore.digitalEntitiesStats);
const materialEntitiesStats = computed(() => assetsCacheStore.materialEntitiesStats);
const projectsStats = computed(() => assetsCacheStore.projectsStats);

// Lifecycle - Précharger les données au montage
onMounted(async (): Promise<void> => {
  // Précharger toutes les données pour les statistiques
  await assetsCacheStore.preloadAllAssets();
});
</script>

<style scoped>
/* Animations personnalisées */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }
}

.float-animation {
  animation: float 6s ease-in-out infinite;
}

/* Effet de brillance sur hover */
.group:hover .shine {
  background: linear-gradient(45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.5) 50%,
      transparent 70%);
  animation: shine 0.6s ease-out;
}

@keyframes shine {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}
</style>